/**
 * bodyIssues.js — EVERY PROBLEM THIS SURFACE SHOWS, AND THE CONTROL THAT ENDS IT.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE STANDING RULE THIS FILE EXISTS TO ENFORCE
 *
 * Brian, 2026-10-15: *"if it's not something you can fix it, surface it but I
 * need a cta to actually fix it. Not just tell me and not have a place to
 * actually fix it."*
 *
 * A surfaced problem with no way to act on it is WORSE than not surfacing it,
 * because it teaches people that the board is something you read past. The
 * artwork request panel was exactly that and was torn out for it.
 *
 * ⚠️ SO THIS IS NOT A LIST OF FIXES BOLTED ONTO A LIST OF FLAGS. It is one
 * structure where a flag CANNOT EXIST WITHOUT A CONTROL — `assertNoDeadEnds()`
 * runs at module load and throws, so a kind added without a working control
 * takes the page down in development and fails `check-no-dead-ends.mjs` in the
 * build rather than shipping as another thing to ignore. The rule is enforced
 * by construction, not by remembering.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHAT WAS THERE BEFORE, MEASURED RATHER THAN CHARACTERISED. On 2026-10-15 the
 * Governing Bodies surface rendered ~91 distinct problem signals — every zero,
 * every "no date", every defect badge, every "RECONCILIATION FAILED", every
 * thin-coverage warning, every "no registry entry" card — and the whole surface
 * could call exactly five API functions, three of which wrote anything:
 * `brandMarkIngest` (a logo), `assignmentMapEntry` (the map, zero records) and
 * `repairDefects` (fill-only rules). The `fused` and `misCarded` tables were
 * rendered `aria-disabled="true"`: flagged, with the door nailed shut.
 *
 * ⚠️ AND THE DOORS MOSTLY EXISTED ALREADY. Probing production as a service
 * identity on 2026-10-15 found `/api/verification/stamps`,
 * `/api/championships/archive`, `/api/championships/load`,
 * `/api/champion-update` (with `governing_body` in its operational tier),
 * `/api/assignments/apply-map` and `/api/repair/system/:operation` all live.
 * The dead ends were never a missing capability. They were a missing button.
 *
 * ⚠️ ONE MORE THING THIS FILE REFUSES TO DO: invent work. A kind that measures
 * zero today is still declared — `registry-card-zero` has had no instance since
 * the 2.112 carding work — because a kind that is deleted when it is empty
 * comes back without its control the day the data changes.
 */

import { CHEER_UNSPECIFIED } from '../../lib/cheerAthleticBodies.js'
import { AMBIGUOUS, FUSED_ON_OBJECT, claimantsOf } from '../../lib/bodyRegistryIndex.js'

const S = (v) => (v == null ? '' : String(v)).trim()
const L = (v) => S(v).toLowerCase()
const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')
const rawOf = (c) => (c && c.raw) || c || {}

/* ══════════════════════════════════════════════════════════════════════════
 * 1. THE VERBS — every control this surface can offer, and the live endpoint
 *    behind each one.
 *
 * ⚠️ `api` IS THE NAME OF A FUNCTION IN lib/api.js AND IS CHECKED AGAINST IT.
 * A verb naming an endpoint that does not exist is the same dead end wearing a
 * button, and it is the specific failure Brian described: *"a CTA that opens a
 * form that doesn't persist, or that sends a request into a queue nobody
 * consumes"*. `check-no-dead-ends.mjs` greps lib/api.js for every name below.
 *
 * ⚠️ `gate` IS WHO MAY PRESS IT, AND IT IS NOT DECORATION. Measured against
 * production 2026-10-15: a service identity is refused by apply-map (403
 * "limited to an executive or the named system actor"), by map-entry (403
 * assignment_locked) and by repair/system (403). These run in the browser as
 * the signed-in person on purpose — one click doing what no machine in the
 * estate is allowed to do.
 * ══════════════════════════════════════════════════════════════════════════ */
export const VERBS = {
  'verify-body': {
    label: 'Verify this body',
    cta: 'Verify against its own site',
    api: 'verificationStamps',
    writes: 'a body/verified stamp on every record this body holds, carrying the source URL',
    gate: 'anyone who can update a champion (a person or an engine)',
    needs: ['source'],
    destructive: false,
    dryRunFirst: false,
    /* A stamp with no source is the thing the stamp exists to stop. */
    refuseWithout: 'An http(s) URL on the body\'s own site. A verification with no source is an assertion, and the store rejects it.',
  },
  'assign-rep': {
    label: 'Assign a rep',
    cta: 'Set who works this body',
    api: 'assignmentMapEntry',
    writes: 'the assignment map for this body — who works it and who leads it',
    gate: 'a manager with Manage Assignments',
    needs: ['reps', 'lead', 'reason', 'authorizedBy'],
    destructive: false,
    dryRunFirst: false,
  },
  'apply-map': {
    label: 'Apply the map to these records',
    cta: 'Apply it to the records',
    api: 'assignmentsApplyMap',
    writes: 'sr_assigned_rep on the records the map now covers, with the reason on each one',
    gate: 'an executive, or the one named system actor',
    needs: ['reason'],
    destructive: false,
    dryRunFirst: true,
  },
  'split-key': {
    label: 'Resolve a fused key',
    cta: 'Split these records onto the right body',
    api: 'championUpdate',
    writes: 'governing_body on each named record, one record at a time, prior value recorded',
    gate: 'a manager with data repair',
    needs: ['mapping', 'reason'],
    destructive: false,
    dryRunFirst: true,
  },
  'create-championship': {
    label: 'Create the missing titles',
    cta: 'Add the championships',
    api: 'championshipsLoad',
    writes: 'new championship records, idempotent on sr_championship_key — an existing key is skipped, never duplicated',
    gate: 'a manager with data repair',
    needs: ['items'],
    destructive: false,
    dryRunFirst: true,
  },
  'rule-out-of-scope': {
    label: 'Rule it out of scope',
    cta: 'Rule out of scope, with a reason',
    /* 🔴 WAS `championshipsArchive` UNTIL 2026-10-16. The object had no scope
     * field, so the only way to exclude a title was to archive the record —
     * and the archive route REFUSES a crowned or locked record as "somebody's
     * work, not a machine mistake", which meant the mechanism could not
     * express the commonest case: a real, crowned title SR does not sell
     * into. `sr_scope_status` exists now and the record stays live. */
    api: 'championUpdate',
    writes: 'sr_scope_status = Out of Scope and sr_scope_reason on each record. The record stays LIVE '
      + 'and the ruling is reversible — nothing is archived and nothing is deleted — and it stops '
      + 'counting toward coverage',
    gate: 'an executive, or a manager with Data Repair',
    needs: ['reason', 'authorizedBy'],
    /* ⚠️ STILL MARKED DESTRUCTIVE, AND MORE DEFENSIBLY THAN WHEN IT ARCHIVED.
     * It is a reversible field write, which is exactly why it is tempting to
     * treat as free — but it takes records out of every count the business
     * runs on, so it is confirmed like one. */
    destructive: true,
    dryRunFirst: true,
    refuseWithout: 'A reason of at least a sentence. The reason IS the ruling: an exclusion that lives '
      + 'only in somebody’s memory is re-worked by the next lane.',
  },
  'repair-defect': {
    label: 'Repair the defect',
    cta: 'Show me what would change',
    api: 'repairDefects',
    writes: 'only the fields a named repair rule fills, and only where they are blank',
    gate: 'a manager with data repair',
    needs: ['rule'],
    destructive: false,
    dryRunFirst: true,
  },
  'fill-blank': {
    label: 'Fill what can be derived',
    cta: 'Run the blank-fill',
    api: 'repairSystemRun',
    writes: 'one named field, only where it is blank and only where exactly one answer resolves',
    gate: 'an executive, or the one named system actor',
    needs: ['operation'],
    destructive: false,
    dryRunFirst: true,
  },
  'add-mark': {
    label: 'Host the mark',
    cta: 'Pull into HubSpot',
    api: 'brandMarkIngest',
    writes: 'the logo file into HubSpot File Manager, so the board never renders a borrowed URL',
    gate: 'anyone who can run a system action',
    needs: ['sourceUrl'],
    destructive: false,
    dryRunFirst: false,
  },
}

/* ══════════════════════════════════════════════════════════════════════════
 * 2. A FLAG IS NOT THE SAME THING AS A DESCRIPTOR, AND CONFLATING THEM IS HOW
 *    A "NO DEAD ENDS" RULE BECOMES UNSATISFIABLE.
 *
 * `multi-state title`, `dormant`, `http only`, `searched for independents`:
 * these are FACTS ABOUT THE BODY, not defects in our data. A NEPSAC title
 * genuinely is multi-state; there is nothing to fix and offering a button
 * would imply there were. They stay as they are, and they are listed here by
 * name so that "it has no control" is a recorded decision rather than an
 * oversight somebody has to re-discover.
 *
 * The test, applied to every signal on the surface: does it assert that
 * SOMETHING IS WRONG WITH WHAT WE HOLD? If yes it is an issue and needs a
 * door. If it merely describes the body, it is a descriptor.
 * ══════════════════════════════════════════════════════════════════════════ */
export const DESCRIPTORS = Object.freeze([
  'multi-state title', 'dormant', 'http only', 'searched for independents',
  'no website — exists, unlinkable',
])

/* ══════════════════════════════════════════════════════════════════════════
 * 3. THE KINDS. Every one declares a `controls` array, and every entry in it
 *    is a key of VERBS. There is no shape for a kind without one.
 * ══════════════════════════════════════════════════════════════════════════ */
export const ISSUE_KINDS = {
  'body-never-verified': {
    label: 'Never verified against its own site',
    severity: 'warn',
    scope: 'body',
    controls: ['verify-body'],
    sentence: (i) => `Nothing has ever confirmed this body's name against its own published site. `
      + `All ${nf(i.count)} of its stamps read inferred or unverified, and zero read verified — `
      + `which is a body nobody has checked, not a body with nothing to check.`,
  },
  'body-unassigned': {
    label: 'Nobody works this body',
    severity: 'warn',
    scope: 'body',
    controls: ['assign-rep', 'apply-map'],
    sentence: (i) => `Unassigned — all ${nf(i.count)} ${i.count === 1 ? 'record' : 'records'}, and `
      + 'not one of them names a rep. Setting the roster records WHO WORKS IT; applying the map is '
      + 'what puts a name on the records.',
  },
  'body-partly-unassigned': {
    label: 'Part of this body has no rep',
    severity: 'warn',
    scope: 'body',
    controls: ['assign-rep', 'apply-map'],
    sentence: (i) => `${nf(i.count)} of this body's ${nf(i.of)} records name no rep. The map may `
      + 'already cover them — applying it says so, and a dry run reports the number before anything moves.',
  },
  'no-registry-entry': {
    label: 'Holds records, has no registry row',
    severity: 'warn',
    scope: 'body',
    controls: ['assign-rep', 'verify-body', 'rule-out-of-scope'],
    /* ⚠️ THE SENTENCE SAYS WHAT THE REGISTRY ROW IS AND IS NOT WORTH, because
     * the honest control here is not "create the row". The row is a code
     * change and has no endpoint — but since 2.112 it is also no longer the
     * gate: the card is drawn from the object, so every action the row would
     * have unlocked is already available on this card. Saying so is the
     * difference between a door and an apology. */
    sentence: (i) => `${nf(i.count)} records name this body and the registry holds no row for it. `
      + 'The row is a code change with no endpoint — and since the cards are drawn from the object '
      + 'rather than the registry, it is no longer what gates the work: assigning, verifying and '
      + 'scope-ruling all work from here today.',
  },
  'fused-key': {
    label: 'One key, more than one body',
    severity: 'bad',
    scope: 'body',
    controls: ['split-key', 'rule-out-of-scope'],
    sentence: (i) => `${nf(i.count)} records carry "${i.bodyKey}", a string more than one `
      + `organisation answers to${i.claimants?.length ? ` — ${i.claimants.join(' / ')}` : ''}`
      + `${i.jurisdictions ? `; on the records: ${i.jurisdictions}` : ''}. No single card can honestly `
      + 'hold them and none has been created, so each record is re-pointed at the body it actually '
      + 'belongs to — which is how the WIAA, MHSAA (public), IHSAA (public) and CAA keys went to zero.',
  },
  'mis-carded': {
    label: 'On a card only by dropping its qualifier',
    severity: 'bad',
    scope: 'body',
    controls: ['split-key'],
    sentence: (i) => `${nf(i.count)} records reach a card by stripping the qualifier off a colliding `
      + 'acronym. That resolved 27 Michigan championships onto Mississippi once. Re-pointing writes '
      + 'the qualified body string the object already uses elsewhere.',
  },
  'body-blank': {
    label: 'No governing body on the record',
    severity: 'bad',
    scope: 'page',
    controls: ['fill-blank', 'split-key', 'rule-out-of-scope'],
    sentence: (i) => `${nf(i.count)} records carry no governing body at all, so no card on any tab `
      + 'can hold them. The blank-fill resolves the ones whose own title names a body we already '
      + 'hold; the rest are named, one at a time, or ruled out.',
  },
  'body-unspecified': {
    label: 'Governing body recorded as "Unspecified"',
    severity: 'bad',
    scope: 'page',
    controls: ['split-key', 'rule-out-of-scope'],
    sentence: (i) => `${nf(i.count)} records say "Unspecified" where the sanctioning body goes. `
      + 'That is not an organisation and no registry entry can ever match it — it is a value that '
      + 'has to be replaced with the real producer, or the record ruled out.',
  },
  'state-missing': {
    label: 'A state-level title that names no state',
    severity: 'bad',
    scope: 'page',
    controls: ['fill-blank', 'repair-defect'],
    sentence: (i) => `${nf(i.count)} state-level ${i.count === 1 ? 'record names' : 'records name'} `
      + 'no state, so no card on the high-school board can hold them. The fill resolves only the ones '
      + 'that land on exactly ONE jurisdiction; a multi-state body is left blank on purpose.',
  },
  'thin-coverage': {
    label: 'Fewer titles than its peers',
    severity: 'warn',
    scope: 'body',
    controls: ['create-championship', 'rule-out-of-scope'],
    sentence: (i) => `${nf(i.count)} championships against a median of ${nf(i.median)} for comparable `
      + 'bodies. Either titles are missing and can be added, or SR does not sell into them and the '
      + 'gap should be ruled out so it stops inflating the denominator.',
  },
  'registry-card-zero': {
    label: 'A card showing zero',
    severity: 'bad',
    scope: 'body',
    controls: ['create-championship', 'split-key', 'rule-out-of-scope'],
    /* ⚠️ DECLARED WITH NO INSTANCES. Measured 2026-10-16 against all 286
     * registry entities: not one is at zero. The kind stays because a kind
     * deleted while empty comes back without its control. */
    sentence: (i) => `This card holds nothing. ${i.detail || ''} Which of the three kinds of zero `
      + 'it is decides the work: acquire the titles, re-point records that exist under another '
      + 'spelling, or rule the body out of scope.',
  },
  'card-defects': {
    label: 'Records with a blocking defect',
    severity: 'bad',
    scope: 'body',
    controls: ['repair-defect'],
    sentence: (i) => `${nf(i.count)} ${i.count === 1 ? 'record has' : 'records have'} a blocking `
      + 'defect on this body. The rules that can fill a blank run from here, dry run first; the ones '
      + 'that need a judgement are named as needing one.',
  },
  'assignment-mismatch': {
    label: 'The two numbers on this card disagree',
    severity: 'bad',
    scope: 'body',
    controls: ['apply-map'],
    /* The dry run IS the diagnosis: it reports, per record, what the map says
     * against what is stored, which is exactly the difference the stamp
     * cannot explain. */
    sentence: (i) => `The assignment breakdown (${nf(i.count)}) does not add up to the championships `
      + `counted beside it (${nf(i.of)}), so one of the two is wrong and neither should be quoted. `
      + 'A dry-run apply reports what the map believes about every one of these records.',
  },
  'resolver-disagreement': {
    /* ⚠️ "the assigned rep", not "the owner". A rep is ASSIGNED a record; they
     * do not own it, and check-ownership-language.mjs holds the whole product
     * to that — correctly, because territory language is how an assignment
     * quietly becomes a claim. */
    label: 'The resolver and the record disagree about the assigned rep',
    severity: 'warn',
    scope: 'body',
    controls: ['apply-map'],
    sentence: (i) => `On ${nf(i.count)} records the assignment map resolves to a different rep than `
      + 'the one stored. Neither is automatically right — the stored value may be a human override, '
      + 'which automation must never take. A dry run shows both; applying it is an explicit human choice.',
  },
  'no-mark': {
    label: 'No hosted mark',
    severity: 'muted',
    scope: 'body',
    controls: ['add-mark'],
    sentence: () => 'This card renders SR-set type because no logo is licensed and hosted by us. '
      + 'Pulling one fetches the file server-side, so the board never renders a borrowed URL.',
  },
}

/* ══════════════════════════════════════════════════════════════════════════
 * 4. THE ENFORCEMENT. Runs at import. A kind with no control, an empty
 *    control list, or a control naming a verb that does not exist is not a
 *    style problem — it is the defect this module was built to make
 *    impossible, so it throws rather than warns.
 * ══════════════════════════════════════════════════════════════════════════ */
export function assertNoDeadEnds(kinds = ISSUE_KINDS, verbs = VERBS) {
  const faults = []
  for (const [id, k] of Object.entries(kinds)) {
    if (!Array.isArray(k.controls) || k.controls.length === 0) {
      faults.push(`${id}: declares no control. A flag whose only affordance is reading it is a bug.`)
      continue
    }
    for (const c of k.controls) {
      if (!verbs[c]) faults.push(`${id}: control "${c}" is not a verb. A button wired to nothing is the dead end with extra steps.`)
      else if (!verbs[c].api) faults.push(`${id}: verb "${c}" names no API function.`)
    }
    if (typeof k.sentence !== 'function') faults.push(`${id}: has no sentence.`)
  }
  if (faults.length) throw new Error(`bodyIssues: ${faults.length} dead end(s)\n  - ${faults.join('\n  - ')}`)
  return true
}
assertNoDeadEnds()

/* ══════════════════════════════════════════════════════════════════════════
 * 5. BUILDING AN ISSUE.
 *
 * ⚠️ `controls` IS RESOLVED HERE, NOT IN THE COMPONENT. If the UI decided
 * which buttons to draw, a renderer that forgot one would silently recreate
 * the dead end for that kind only — and nothing would fail. Resolving in the
 * model means the component cannot render an issue without also rendering
 * every control that issue declares.
 * ══════════════════════════════════════════════════════════════════════════ */
function issue(kindId, ctx) {
  const k = ISSUE_KINDS[kindId]
  /* Not a soft fallback: an unknown kind is a programming error and a silent
   * skip would drop the flag entirely, which is strictly worse than the dead
   * end it replaced. */
  if (!k) throw new Error(`bodyIssues: unknown kind "${kindId}"`)
  return {
    kind: kindId,
    id: `${kindId}:${ctx.bodyKey || ctx.scopeKey || 'page'}`,
    label: k.label,
    severity: k.severity,
    scope: k.scope,
    sentence: k.sentence(ctx),
    controls: k.controls.map((v) => ({ verb: v, ...VERBS[v] })),
    ...ctx,
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * 6. THE ISSUES ON ONE CARD.
 *
 * Everything is passed in rather than re-derived. A second join is a second
 * answer to one question, and the per-card stamp disagreeing with the number
 * six lines above it on the same card is the defect this whole surface was
 * built to remove.
 * ══════════════════════════════════════════════════════════════════════════ */
export function cardIssues({
  entity, rows = [], assign = null, tally = null, zero = null, coverage = null,
  defects = 0, verification = null, hosted = false,
} = {}) {
  if (!entity) return []
  const out = []
  const bodyKey = entity.key || entity.acronym || entity.name
  const base = { bodyKey, entity, records: rows }

  /* ── never verified. Read off the live verification rollup, never guessed:
   * a body with NO row has no stamps at all, which is a different fact from
   * a body stamped inferred, and the two must not collapse. */
  if (verification && verification.verified === 0 && verification.stamped > 0) {
    /* ⚠️ THE STAMP'S OWN KEY, NOT THE CARD'S. The card is keyed on a registry
     * key (`AL-AHSAA`); the store is keyed on the qualified governing-body
     * string the records carry (`AHSAA (public)`). Writing the card's key
     * would file the verification under a body the rollup has never heard of,
     * so the flag would still read zero-verified after a successful write —
     * a control that runs, reports success, and does not clear its own flag. */
    out.push(issue('body-never-verified', { ...base, bodyKey: verification.bodyKey || bodyKey,
      count: verification.stamped,
      inferred: verification.inferred || 0, unverified: verification.unverified || 0 }))
  }

  /* ── assignment */
  if (assign) {
    if (assign.state === 'unassigned' && assign.records > 0) {
      out.push(issue('body-unassigned', { ...base, count: assign.records }))
    } else if (assign.state === 'partial' && assign.unassigned > 0) {
      out.push(issue('body-partly-unassigned', { ...base, count: assign.unassigned, of: assign.records }))
    }
    /* The reconciliation failure. Fires only when the two numbers on the card
     * genuinely disagree — it is a bug signal, and a dry-run apply is the only
     * thing that can say which of the two is lying. */
    if (tally && (assign.records !== tally.held || assign.reconciled === false)) {
      out.push(issue('assignment-mismatch', { ...base, count: assign.records, of: tally.held }))
    }
  }

  /* ── the three kinds of zero, and the thin card that is not a zero */
  if (zero) out.push(issue('registry-card-zero', { ...base, count: 0, zeroKind: zero.kind, detail: zero.detail }))
  if (coverage) {
    out.push(issue('thin-coverage', { ...base, count: coverage.held ?? (tally ? tally.held : 0),
      median: coverage.median ?? null, shortfall: coverage.shortfall ?? null }))
  }

  if (defects > 0) out.push(issue('card-defects', { ...base, count: defects }))
  if (!hosted && !entity.rollup) out.push(issue('no-mark', { ...base, count: 0 }))

  return out
}

/* ══════════════════════════════════════════════════════════════════════════
 * 7. THE ISSUES THE WHOLE PAGE OWNS — the ones no single card can hold,
 *    which is precisely why they used to be unreachable.
 * ══════════════════════════════════════════════════════════════════════════ */
/**
 * ⭐ A RULED-OUT RECORD IS NOT A PROBLEM TO SOLVE.
 *
 * The scope rules are explicit about the two wrong homes for an exclusion:
 * logged as a GAP it re-enters the creation queue every wave, and logged as
 * an honest ZERO it libels a body that genuinely crowns the title. Both of
 * those are what happens if a record ruled Out of Scope keeps flowing into
 * this model — it would be counted as a blank body, a missing state, a
 * defect, every wave, forever.
 *
 * So scope is filtered FIRST, before any issue is computed, and the number
 * filtered is RETURNED rather than silently dropped. A filter nobody can see
 * the size of is the same silent zero this whole surface exists to end.
 */
export function partitionScope(records = []) {
  const live = []
  const ruledOut = []
  for (const c of records) {
    if (String(rawOf(c).sr_scope_status ?? c?.scopeStatus ?? '').trim() === 'Out of Scope') ruledOut.push(c)
    else live.push(c)
  }
  return { live, ruledOut }
}

export function pageIssues({ records = [], totals = null, orphans = [], derived = null } = {}) {
  const out = []

  /* ⚠️ EVERY COUNT BELOW IS OVER `live`, NEVER over `records`. See
   * partitionScope: an exclusion that still counts is not an exclusion. */
  const { live, ruledOut } = partitionScope(records)
  out.ruledOutOfScope = ruledOut.length

  const blank = []
  const unspecified = []
  for (const c of live) {
    const v = S(rawOf(c).sr_conference)
    if (!v) blank.push(c)
    else if (v === CHEER_UNSPECIFIED) unspecified.push(c)
  }
  if (blank.length) {
    out.push(issue('body-blank', { scopeKey: 'blank-body', count: blank.length, records: blank,
      operation: 'fill-governing-body-derivable' }))
  }
  if (unspecified.length) {
    out.push(issue('body-unspecified', { scopeKey: 'unspecified-body', count: unspecified.length, records: unspecified }))
  }
  /* Filtered here too, not trusted to the caller. `orphans` arrives already
   * computed, and a predicate applied in one place and not the other is how
   * two numbers describing the same thing start disagreeing. */
  const liveOrphans = partitionScope(orphans).live
  if (liveOrphans.length) {
    out.push(issue('state-missing', { scopeKey: 'state-missing', count: liveOrphans.length, records: liveOrphans,
      operation: 'fill-state-single-jurisdiction' }))
  }

  /* ══════════════════════════════════════════════════════════════════════
   * THE FUSED KEYS, COUNTED OFF BOTH FIELDS — and this is a CORRECTION.
   *
   * 🔴 THE DERIVED TAB'S FUSED TABLE READ ZERO WHILE 82 RECORDS SAT ON BARE
   * COLLIDING ACRONYMS. Measured 2026-10-16T02:28Z against the live
   * snapshot: `MHSAA` 18, `IHSAA` 29, `CCAA` 35. `derivedBodies` partitions
   * the object on `sr_conference` alone — correctly, because that is what
   * makes its arithmetic close — so a record carrying the fused string in
   * `governing_body` is invisible to it, and `fusedKeyCount: 0` was a true
   * statement about the wrong field read as a true statement about the
   * estate. A zero that is produced by not looking is the exact defect this
   * whole surface exists to end, and it was being rendered by the tab whose
   * job is to end it.
   *
   * ⚠️ DERIVED FROM THE REGISTRY'S OWN SETS, NEVER A LIST HERE. `AMBIGUOUS`
   * is every token more than one differently-named organisation claims, and
   * `FUSED_ON_OBJECT` is every string the object stores that spans bodies.
   * Both are built from the five registry arrays, so this cannot drift from
   * what the registry holds — a hand list here would go stale the first time
   * somebody adds an association.
   * ══════════════════════════════════════════════════════════════════════ */
  const fusedRows = new Map()
  for (const c of live) {
    const r = rawOf(c)
    for (const field of ['governing_body', 'sr_conference']) {
      const v = S(r[field])
      if (!v) continue
      const k = L(v)
      if (!AMBIGUOUS.has(k) && !FUSED_ON_OBJECT.has(k)) continue
      let e = fusedRows.get(v)
      if (!e) fusedRows.set(v, e = { label: v, records: [], states: new Map() })
      e.records.push(c)
      const st = S(r.sr_state_location) || S(r.sr_state) || '(no state)'
      e.states.set(st, (e.states.get(st) || 0) + 1)
      break /* one record counts once, even if both fields carry it */
    }
  }
  for (const e of [...fusedRows.values()].sort((a, b) => b.records.length - a.records.length)) {
    out.push(issue('fused-key', {
      scopeKey: `fused:${e.label}`, bodyKey: e.label, count: e.records.length, records: e.records,
      jurisdictions: [...e.states.entries()].map(([s, n]) => `${s} ${n}`).join(' · '),
      /* Who actually claims this token, so the split is a choice between
       * named organisations rather than a guess. */
      claimants: claimantsOf(e.label).map((x) => x.name),
    }))
  }

  /* ── the bodies with no registry row, and the two tables that shipped
   * `aria-disabled="true"` — flagged, with the door nailed shut.
   *
   * ⚠️ `derived.fusedKeys` IS DELIBERATELY NOT RE-EMITTED HERE. It is the
   * sr_conference-only view of the same fact and would double-count every
   * row the scan above already found. */
  if (derived) {
    for (const m of derived.misCarded || []) {
      out.push(issue('mis-carded', { scopeKey: `miscarded:${m.body}`, bodyKey: m.body, count: m.count || 0,
        records: m.records || [], collision: m.collision, card: m.card }))
    }
  }
  /* The headline blind spot stays a single page-level issue rather than 471
   * identical ones — the per-body door is the derived card itself. */
  if (totals && totals.noCardBodies > 0) {
    out.push(issue('no-registry-entry', { scopeKey: 'no-registry-entry', count: totals.noCard,
      bodies: totals.noCardBodies, withRep: totals.noCardWithRep, noRep: totals.noCardNoRep }))
  }
  return out
}

/* ══════════════════════════════════════════════════════════════════════════
 * 8. THE ASSERTION STEP 3 ASKS FOR, AS A FUNCTION RATHER THAN A WALKTHROUGH.
 *
 * "Every flag rendered has a control, nothing is hidden, nothing is capped at
 * the first 100 of n, and every count reconciles to a total." A human walk
 * proves it once; this proves it on every render and in the build.
 * ══════════════════════════════════════════════════════════════════════════ */
export function auditIssues(issues) {
  const withoutControl = issues.filter((i) => !i.controls || !i.controls.length)
  const byKind = new Map()
  for (const i of issues) {
    const e = byKind.get(i.kind) || { kind: i.kind, instances: 0, records: 0, controls: (i.controls || []).map((c) => c.verb) }
    e.instances += 1
    e.records += Number(i.count) || 0
    byKind.set(i.kind, e)
  }
  return {
    issues: issues.length,
    kinds: byKind.size,
    withoutControl: withoutControl.length,
    deadEnds: withoutControl.map((i) => i.id),
    /* The sum is of ISSUE counts, not of distinct records: one record can be
     * blank-bodied AND state-missing, and pretending otherwise would be the
     * silent double-count this surface already warns about on its defect line. */
    recordsFlagged: issues.reduce((a, i) => a + (Number(i.count) || 0), 0),
    byKind: [...byKind.values()].sort((a, b) => b.records - a.records),
    clean: withoutControl.length === 0,
  }
}

export const _internals = { S, L, nf, rawOf }

